import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Alert, Chip, Tabs, Tab, Card, CardContent, Grid, LinearProgress, Tooltip
} from '@mui/material';
import {
  Close, Refresh, Download, Visibility, Delete, Analytics,
  TrendingUp, Person, AccessTime, CheckCircle
} from '@mui/icons-material';

const FormResponses = ({ open, onClose, form, responses, onRefresh, loading }) => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedResponse, setSelectedResponse] = useState(null);

  // Debug: Log form fields and responses to see structure
  React.useEffect(() => {
    if (form && responses?.length) {
      console.log('Form fields:', JSON.stringify(form.fields?.map(f => ({ id: f.id, _id: f._id, label: f.label })), null, 2));
      console.log('First response answers:', JSON.stringify(responses[0]?.answers, null, 2));
    }
  }, [form, responses]);

  const getAnswerDisplay = (answer) => {
    if (Array.isArray(answer)) return answer.join(', ');
    if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
    return answer || '-';
  };

  const calculateStats = () => {
    if (!responses?.length || !form?.fields?.length) return null;

    const stats = {
      total: responses.length,
      completed: responses.filter(r => r.answers?.length === form.fields.length).length,
      avgCompletionTime: '2m 30s', // Placeholder
      fieldStats: {}
    };

    // Helper to find answer supporting both formats
    const findAnswer = (r, field) => {
      const answerObj = r.answers?.find(a => 
        a.question === field.label || 
        a.fieldLabel === field.label || 
        a.fieldId === field.id ||
        a.fieldId === field._id  // MongoDB might use _id
      );
      return answerObj?.answer || answerObj?.value;
    };

    form.fields.forEach(field => {
      if (['RATING', 'SCALE'].includes(field.type)) {
        const values = responses
          .map(r => findAnswer(r, field))
          .filter(v => v !== undefined && v !== null && v !== '')
          .map(Number);
        
        if (values.length > 0) {
          stats.fieldStats[field.label] = {
            avg: (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1),
            count: values.length
          };
        }
      } else if (['MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN'].includes(field.type)) {
        const counts = {};
        responses.forEach(r => {
          const answer = findAnswer(r, field);
          const answers = Array.isArray(answer) ? answer : [answer];
          answers.forEach(a => {
            if (a) counts[a] = (counts[a] || 0) + 1;
          });
        });
        stats.fieldStats[field.label] = counts;
      }
    });

    return stats;
  };

  const stats = calculateStats();

  const exportToCSV = () => {
    if (!responses?.length || !form?.fields) return;

    const headers = ['Submitted At', 'Respondent', 'Contact', ...form.fields.map(f => f.label)];
    const rows = responses.map(r => [
      new Date(r.submittedAt).toLocaleString(),
      r.customerName || r.customerId?.name || 'Anonymous',
      r.customerMobile || r.respondentEmail || '-',
      ...form.fields.map(f => {
        // Support both old format (question) and new format (fieldLabel/fieldId)
        const answerObj = r.answers?.find(a => 
          a.question === f.label || 
          a.fieldLabel === f.label || 
          a.fieldId === f.id ||
          a.fieldId === f._id  // MongoDB might use _id
        );
        return getAnswerDisplay(answerObj?.answer || answerObj?.value);
      })
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.title}_responses.csv`;
    a.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{form?.title}</Typography>
          <Typography variant="body2" color="textSecondary">
            {responses?.length || 0} response{responses?.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Download />} onClick={exportToCSV} disabled={!responses?.length}>
            Export CSV
          </Button>
          <Button startIcon={<Refresh />} onClick={onRefresh}>
            Refresh
          </Button>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && <LinearProgress />}

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<Analytics />} label="Summary" iconPosition="start" />
          <Tab icon={<Person />} label="Individual" iconPosition="start" />
        </Tabs>

        {/* Summary Tab */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            {!responses?.length ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No responses yet. Share your form to start collecting responses.
              </Alert>
            ) : (
              <>
                {/* Stats Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary">{stats?.total || 0}</Typography>
                        <Typography color="textSecondary">Total Responses</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="success.main">{stats?.completed || 0}</Typography>
                        <Typography color="textSecondary">Completed</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="info.main">
                          {stats?.total ? Math.round((stats.completed / stats.total) * 100) : 0}%
                        </Typography>
                        <Typography color="textSecondary">Completion Rate</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="warning.main">{form?.fields?.length || 0}</Typography>
                        <Typography color="textSecondary">Questions</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Field Stats */}
                {form?.fields?.map((field, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {field.label}
                    </Typography>
                    
                    {['RATING', 'SCALE'].includes(field.type) && stats?.fieldStats?.[field.label] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h4" color="primary">
                          {stats.fieldStats[field.label].avg}
                        </Typography>
                        <Typography color="textSecondary">
                          average ({stats.fieldStats[field.label].count} responses)
                        </Typography>
                      </Box>
                    )}

                    {['MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN'].includes(field.type) && stats?.fieldStats?.[field.label] && (
                      <Box>
                        {Object.entries(stats.fieldStats[field.label]).map(([option, count]) => (
                          <Box key={option} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">{option}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {count} ({Math.round((count / responses.length) * 100)}%)
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={(count / responses.length) * 100}
                              sx={{ height: 8, borderRadius: 1 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}

                    {!['RATING', 'SCALE', 'MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN'].includes(field.type) && (
                      <Typography color="textSecondary" variant="body2">
                        {responses.filter(r => r.answers?.find(a => a.question === field.label)?.answer).length} responses
                      </Typography>
                    )}
                  </Paper>
                ))}
              </>
            )}
          </Box>
        )}

        {/* Individual Responses Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            {!responses?.length ? (
              <Alert severity="info">No responses yet.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Respondent</TableCell>
                      {form?.fields?.slice(0, 4).map((f, i) => (
                        <TableCell key={i} sx={{ maxWidth: 150 }}>
                          <Tooltip title={f.label}>
                            <Typography noWrap>{f.label}</Typography>
                          </Tooltip>
                        </TableCell>
                      ))}
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {responses.map((response) => (
                      <TableRow key={response._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime fontSize="small" color="action" />
                            {new Date(response.submittedAt).toLocaleDateString()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person fontSize="small" color="action" />
                            <Box>
                              <Typography variant="body2">
                                {response.customerName || response.customerId?.name || 'Anonymous'}
                              </Typography>
                              {(response.customerMobile || response.respondentEmail) && (
                                <Typography variant="caption" color="textSecondary">
                                  {response.customerMobile || response.respondentEmail}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        {form?.fields?.slice(0, 4).map((f, i) => {
                          // Support both old format (question) and new format (fieldLabel/fieldId)
                          const answer = response.answers?.find(a => 
                            a.question === f.label || 
                            a.fieldLabel === f.label || 
                            a.fieldId === f.id ||
                            a.fieldId === f._id  // MongoDB might use _id
                          );
                          return (
                            <TableCell key={i} sx={{ maxWidth: 150 }}>
                              <Typography noWrap variant="body2">
                                {getAnswerDisplay(answer?.answer || answer?.value)}
                              </Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => setSelectedResponse(response)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Response Detail Dialog */}
      <Dialog open={!!selectedResponse} onClose={() => setSelectedResponse(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>Response Details</Typography>
          <IconButton onClick={() => setSelectedResponse(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedResponse && (
            <Box>
              <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                <Typography variant="body2" color="textSecondary">
                  Submitted: {new Date(selectedResponse.submittedAt).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Respondent: {selectedResponse.customerName || selectedResponse.customerId?.name || 'Anonymous'}
                  {(selectedResponse.customerMobile || selectedResponse.respondentEmail) && (
                    <> ({selectedResponse.customerMobile || selectedResponse.respondentEmail})</>)}
                </Typography>
              </Box>
              {form?.fields?.map((field, index) => {
                // Support both old format (question) and new format (fieldLabel/fieldId)
                const answer = selectedResponse.answers?.find(a => 
                  a.question === field.label || 
                  a.fieldLabel === field.label || 
                  a.fieldId === field.id ||
                  a.fieldId === field._id  // MongoDB might use _id
                );
                return (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">{field.label}</Typography>
                    <Typography>{getAnswerDisplay(answer?.answer || answer?.value)}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default FormResponses;
